import {Record} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';
import * as NewContactActions from 'web-app/react/profile/child-profile/new-contact/actions';
import * as ChildActions from 'web-app/react/entities/children/actions';

import {ExtendedChildCheckin as ExtendedChildCheckinRecord} from './models/extended-child-checkin';
import {ChildRelation} from './models/shared';
import * as Actions from './actions';

interface ChildPickupModal {
    readonly isFetchingCheckin: boolean;
    readonly childSignIn: ExtendedChildCheckinRecord;
    readonly isVisible: boolean;
    readonly childId: string;
    readonly goHomeWithChildId: string;
    readonly requirePickupInfo: boolean;
    readonly fetchingGoHomeWithChild: boolean;
    readonly peoplePickerGoHomeWithChildId: string;
    readonly isUpdating: boolean;
}

export class ChildPickupModalState extends Record<ChildPickupModal>({
    isFetchingCheckin: false,
    childSignIn: new ExtendedChildCheckinRecord(),
    isVisible: false,
    childId: '',
    goHomeWithChildId: '',
    requirePickupInfo: false,
    fetchingGoHomeWithChild: false,
    peoplePickerGoHomeWithChildId: '',
    isUpdating: false,
}) {}

const updateState = (
    state: ChildPickupModalState,
    newSignInProps: Partial<ExtendedChildCheckinRecord>,
    other?: Partial<Omit<ChildPickupModal, 'childSignIn'>>,
): ChildPickupModalState => {
    const updatedSignIn = state.childSignIn.merge(newSignInProps);
    return state.merge({
        childSignIn: updatedSignIn,
        ...(other || {}),
    });
};

export default makeReducer<ChildPickupModalState>(
    [
        match(Actions.show, (state, {payload}) => {
            return state.merge({
                isVisible: true,
                childId: payload.childId,
            });
        }),
        match(Actions.hide, state => {
            return new ChildPickupModalState({
                requirePickupInfo: state.requirePickupInfo,
            });
        }),
        match(Actions.selectPickupPerson, (state, {payload}) => {
            return updateState(
                state,
                {
                    pickupRelationId: payload.pickupRelationId,
                },
                {goHomeWithChildId: ''},
            );
        }),
        match(Actions.selectGoHomeWithChildId, (state, {payload}) => {
            return updateState(
                state,
                {pickupRelationId: ''},
                {
                    goHomeWithChildId: payload.goHomeWithChildId,
                    peoplePickerGoHomeWithChildId: payload.goHomeWithChildId,
                },
            );
        }),
        match(Actions.updateCheckin, state => {
            return state.merge({
                isUpdating: true,
            });
        }),
        match(Actions.updateCheckinSuccess, (state, {payload}) => {
            return updateState(state, {checkedIn: true, ...payload.response}, {isUpdating: false});
        }),
        match(Actions.updateCheckinFailed, state => {
            return state.merge({isUpdating: false});
        }),
        match(Actions.fetchExtendedCheckin, state => {
            return state.merge({
                isFetchingCheckin: true,
            });
        }),
        match(Actions.fetchExtendedCheckinSuccess, (state, {payload}) => {
            const signIn = payload.response;
            const goHomeWithChildId = signIn && signIn.goHomeWithChildId;

            return state.merge({
                isFetchingCheckin: false,
                childSignIn: signIn,
                peoplePickerGoHomeWithChildId: goHomeWithChildId,
                goHomeWithChildId,
            });
        }),
        match(Actions.fetchExtendedCheckinFailed, state => {
            return state.merge({
                isFetchingCheckin: false,
            });
        }),
        match(ChildActions.emberFetchSuccess, (state, {payload}) => {
            return state.merge({
                requirePickupInfo: payload.group.requirePickupInfo,
            });
        }),
        match(NewContactActions.createContactSuccess, (state, {payload}) => {
            const {image, name, relationId} = payload.newContact;

            return updateState(state, {
                relations: state.childSignIn.relations.push(
                    new ChildRelation({
                        relationId,
                        image: image.small,
                        name: name.firstName,
                    }),
                ),
            });
        }),
    ],
    new ChildPickupModalState(),
);
